import React, { useState, useEffect, useRef } from 'react';
import MainLayout from '../../layouts/MainLayout';
import {
  Box, Container, Paper, Typography, Grid, FormControl, InputLabel, Select, MenuItem, TextField, Button, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, CircularProgress,
  FormControlLabel,
  Checkbox,
  ToggleButtonGroup, ToggleButton
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { colaboradorService, Colaborador } from '../../services/colaboradorService';
import { ramoService} from '../../services/ramoService';
import { clienteService, Cliente } from '../../services/clienteService';
import { agenciaService } from '../../services/agenciaService';
import { empresaService, Empresa } from '../../services/empresaService';
import { Agencia } from '../../types/agencia';
import { relatorioService } from '../../services/relatorioService';
import { Ramo } from '../../types/ramo';
import { formatarMoeda } from '../../utils/formatters';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';


// Interface para o resultado do relatório
interface RelatorioResultado {
  id: number;
  clientenome: string;
  vendedornome: string;
  ramonome: string;
  valorvendido: number;
}

export default function RelatorioPage() {
  const [colaboradores, setColaboradores] = useState<Colaborador[]>([]);
  const [ramos, setRamos] = useState<Ramo[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [agencias, setAgencias] = useState<Agencia[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [loading, setLoading] = useState(false);
  const [buscou, setBuscou] = useState(false);
  const pdfRef = useRef<HTMLDivElement | null>(null);
// no JSX: <div ref={pdfRef}><TableContainer> ... </TableContainer></div>


  // Resultados
  const [resultados, setResultados] = useState<RelatorioResultado[]>([]);
  const [resultadosAnoAnterior, setResultadosAnoAnterior] = useState<RelatorioResultado[]>([]);

  // Filtros
  const [filtroColaborador, setFiltroColaborador] = useState<number | ''>('');
  const [filtroRamo, setFiltroRamo] = useState<number | ''>('');
  const [filtroCliente, setFiltroCliente] = useState<number | ''>('');
  const [filtroAgencia, setFiltroAgencia] = useState<number | ''>('');
  const [filtroEmpresa, setFiltroEmpresa] = useState<number | ''>('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [errosFiltro, setErrosFiltro] = useState({ dataInicio: false, dataFim: false });
  const [compararAnoAnterior, setCompararAnoAnterior] = useState(false);
  const [periodoVisivel, setPeriodoVisivel] = useState<'atual' | 'anterior'>('atual');

  useEffect(() => {
    setLoading(true);
    const carregarDados = async () => {
      try {
        const [
          colaboradoresData, 
          ramosData,
          clientesData,
          agenciasData,
          empresasData
        ] = await Promise.all([
          colaboradorService.listar(),
          ramoService.listarRamos(),
          clienteService.listar(),
          agenciaService.listarAgencias(),
          empresaService.listar()
        ]);
        setColaboradores(colaboradoresData);
        setRamos(ramosData);
        setClientes(clientesData.data);
        setAgencias(agenciasData);
        setEmpresas(empresasData);
      } catch (error) {
        toast.error('Erro ao carregar dados para os filtros.');
      } finally {
        setLoading(false);
      }
    };
    carregarDados();
  }, []);

  const handleBuscar = async () => {
    // Reseta os erros antes de uma nova busca
    setErrosFiltro({ dataInicio: false, dataFim: false });

    // Validação para garantir que ambas as datas sejam preenchidas
    if (!dataInicio || !dataFim) {
      toast.error('As datas "De" e "Até" são obrigatórias para gerar o relatório.');
      setErrosFiltro({ dataInicio: !dataInicio, dataFim: !dataFim });
      return;
    }
    if (dataInicio && dataFim && new Date(dataInicio) > new Date(dataFim)) {
      toast.error('A data "De" não pode ser maior que a data "Até".');
      setErrosFiltro({ dataInicio: true, dataFim: true });
      return;
    }

    setLoading(true);
    setBuscou(false);
    setResultadosAnoAnterior([]); // Limpa os resultados da comparação anterior
    setPeriodoVisivel('atual'); // Reseta a visualização para o período atual

    try {
      const filtrosParaApi = {
        colaboradorId: filtroColaborador,
        ramoId: filtroRamo,
        clienteId: filtroCliente,
        agenciaId: filtroAgencia,
        empresaId: filtroEmpresa,
        dataInicio: dataInicio,
        dataFim: dataFim,
      };

      const dados = await relatorioService.gerar(filtrosParaApi);
      setResultados(dados);

      // Se a comparação estiver habilitada, busca os dados do ano anterior
      if (compararAnoAnterior) {
        const dataInicioAnterior = new Date(dataInicio);
        dataInicioAnterior.setFullYear(dataInicioAnterior.getFullYear() - 1);

        const dataFimAnterior = new Date(dataFim);
        dataFimAnterior.setFullYear(dataFimAnterior.getFullYear() - 1);

        const filtrosAnoAnterior = {
          ...filtrosParaApi,
          dataInicio: dataInicioAnterior.toISOString().split('T')[0],
          dataFim: dataFimAnterior.toISOString().split('T')[0],
        };
        const dadosAnteriores = await relatorioService.gerar(filtrosAnoAnterior);
        setResultadosAnoAnterior(dadosAnteriores);
      }

      setBuscou(true);
      toast.success('Relatório gerado com sucesso!');
    } catch (error) {
      toast.error('Erro ao gerar o relatório.');
    } finally {
      setLoading(false);
    }
  };

  // Calcula o valor total vendido a partir dos resultados
  const valorTotalVendido = resultados.reduce((acc, item) => acc + Number(item.valorvendido), 0);
  const valorTotalVendidoAnoAnterior = resultadosAnoAnterior.reduce((acc, item) => acc + Number(item.valorvendido), 0);

  const crescimento = valorTotalVendidoAnoAnterior > 0
    ? ((valorTotalVendido - valorTotalVendidoAnoAnterior) / valorTotalVendidoAnoAnterior) * 100
    : 0;

  const dadosParaExibir = periodoVisivel === 'atual' ? resultados : resultadosAnoAnterior;

  const handlePeriodoChange = (
    event: React.MouseEvent<HTMLElement>,
    novoPeriodo: 'atual' | 'anterior' | null,
  ) => {
    if (novoPeriodo !== null) setPeriodoVisivel(novoPeriodo);
  };

const handleExportPDF = async () => {
  const element = document.getElementById("testerelatorio");
  if (!element) return;

  const canvas = await html2canvas(element, {
    scale: 2,
  });

  const imgData = canvas.toDataURL("image/png");
  const pdf = new jsPDF("p", "mm", "a4");

  const imgWidth = 210;
  const pageHeight = 297;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;

  let heightLeft = imgHeight;
  let position = 0;

  pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
  heightLeft -= pageHeight;

  while (heightLeft > 0) {
    pdf.addPage();
    position = heightLeft - imgHeight;
    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
  }

  pdf.save("relatorio.pdf");
};




  return (
    <MainLayout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Grid container spacing={3}>
          {/* Seção de Filtros */}
          <Grid size={{xs:12}}>
            <Paper sx={{ p: 2, mb: 4, borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Filtros do Relatório</Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Colaborador</InputLabel>
                    <Select value={filtroColaborador} label="Colaborador" onChange={(e) => setFiltroColaborador(e.target.value as number)}>
                      <MenuItem value=""><em>Todos</em></MenuItem>
                      {colaboradores.map((c) => (<MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Ramo</InputLabel>
                    <Select value={filtroRamo} label="Ramo" onChange={(e) => setFiltroRamo(e.target.value as number)}>
                      <MenuItem value=""><em>Todos</em></MenuItem>
                      {ramos.map((r) => (<MenuItem key={r.id} value={r.id}>{r.nome}</MenuItem>))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Cliente</InputLabel>
                    <Select value={filtroCliente} label="Cliente" onChange={(e) => setFiltroCliente(e.target.value as number)}>
                      <MenuItem value=""><em>Todos</em></MenuItem>
                      {clientes.map((c) => (<MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Agência</InputLabel>
                    <Select value={filtroAgencia} label="Agência" onChange={(e) => setFiltroAgencia(e.target.value as number)}>
                      <MenuItem value=""><em>Todas</em></MenuItem>
                      {agencias.map((a) => (<MenuItem key={a.id} value={a.id}>{a.nome}</MenuItem>))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Empresa</InputLabel>
                    <Select value={filtroEmpresa} label="Empresa" onChange={(e) => setFiltroEmpresa(e.target.value as number)}>
                      <MenuItem value=""><em>Todas</em></MenuItem>
                      {empresas.map((e) => (<MenuItem key={e.id} value={e.id}>{e.nome}</MenuItem>))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                  <TextField
                    fullWidth
                    type="date"
                    label="De *"
                    InputLabelProps={{ shrink: true }}
                    value={dataInicio}
                    onChange={(e) => { setDataInicio(e.target.value); if (errosFiltro.dataInicio) setErrosFiltro(prev => ({ ...prev, dataInicio: false })); }}
                    error={errosFiltro.dataInicio}
                    helperText={errosFiltro.dataInicio ? 'Obrigatório' : ''}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Até *"
                    InputLabelProps={{ shrink: true }}
                    value={dataFim}
                    onChange={(e) => { setDataFim(e.target.value); if (errosFiltro.dataFim) setErrosFiltro(prev => ({ ...prev, dataFim: false })); }}
                    error={errosFiltro.dataFim}
                    helperText={errosFiltro.dataFim ? 'Obrigatório' : ''}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={compararAnoAnterior}
                        onChange={(e) => setCompararAnoAnterior(e.target.checked)}
                      />
                    }
                    label="Comparar com o mesmo período do ano anterior"
                  />
                </Grid>
                <Grid size={{ xs: 12 }} sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start' }}>
                  <Button variant="contained" startIcon={<SearchIcon />} onClick={handleBuscar}>
                    Buscar
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Seção de Resultados (Hello World por enquanto) */}
          {loading && (
            <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Grid>
          )}

          {buscou && !loading && (
            <Grid size={{ xs: 12 }} id='testerelatorio'>
              <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" component="h2" gutterBottom>
                  Resultados do Relatório
                </Typography>

             
                
                {/* Resumo */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid size={{ xs: 12, md: compararAnoAnterior ? 4 : 12 }}>
                    <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="subtitle1" color="text.secondary">Valor Total Vendido (Período Atual)</Typography>
                      <Typography variant="h5" component="p" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        {formatarMoeda(valorTotalVendido)}
                      </Typography>
                    </Paper>
                  </Grid>
                  {compararAnoAnterior && (
                    <>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="subtitle1" color="text.secondary">Valor Total Vendido (Período Anterior)</Typography>
                          <Typography variant="h5" component="p" sx={{ fontWeight: 'bold' }}>{formatarMoeda(valorTotalVendidoAnoAnterior)}</Typography>
                        </Paper>
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Paper elevation={2} sx={{ p: 2, textAlign: 'center', backgroundColor: crescimento >= 0 ? '#e8f5e9' : '#ffebee' }}>
                          <Typography variant="subtitle1" color="text.secondary">Crescimento/Queda (%)</Typography>
                          <Typography variant="h5" component="p" sx={{ fontWeight: 'bold', color: crescimento >= 0 ? 'success.main' : 'error.main' }}>{crescimento.toFixed(2)}%</Typography>
                        </Paper>
                      </Grid>
                    </>
                  )}
                </Grid>

                {/* Botões para alternar a visualização da tabela */}
                {compararAnoAnterior && buscou && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                    <ToggleButtonGroup
                      color="primary"
                      value={periodoVisivel}
                      exclusive
                      onChange={handlePeriodoChange}
                      aria-label="selecionar período do relatório"
                    >
                      <ToggleButton value="atual">Período Atual</ToggleButton>
                      <ToggleButton value="anterior">Período Anterior</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                )}


                {/* Tabela de Resultados */}
                <div ref={pdfRef} id="relatorioDiv">
                  <TableContainer>
                    <Table stickyHeader aria-label="tabela de resultados do relatório">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Cliente</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Vendedor</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Ramo</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Valor Vendido</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dadosParaExibir.length > 0 ? (
                          dadosParaExibir.map((row) => (
                            <TableRow key={row.id} hover>
                              <TableCell>{row.clientenome}</TableCell>
                              <TableCell>{row.vendedornome}</TableCell>
                              <TableCell>{row.ramonome}</TableCell>
                              <TableCell align="right">{formatarMoeda(Number(row.valorvendido))}</TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              Nenhum resultado encontrado para os filtros selecionados.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              
              </Paper>
            </Grid>
          )}

          {buscou && !loading &&( <Button variant="contained" onClick={handleExportPDF}>
              Exportar PDF
            </Button>)}
              
        </Grid>
      </Container>
    </MainLayout>
  );
}